import { useLocation } from "react-router-dom";
import { useEffect } from "react";
import { preloadCriticalImages } from "./utils/imageOptimizer";
import { useBrandColors } from "./hooks/useBrandColors";
import { PerformanceMonitor } from "./components/PerformanceMonitor";
import { NetworkDiagnostics } from "./components/diagnostics/NetworkDiagnostics";
import AppRoutes from "./components/AppRoutes";
import { ApplicationTitle } from "./components/ApplicationTitle";
import { ProfileCheckProvider } from "./contexts/ProfileCheckContext";
import { Toaster } from "./components/ui/toaster";
import { initResourceHints } from "./utils/resourceHintsHelper";
import { LovableEditorDetector } from "./components/LovableEditorDetector";
import { 
  isLovableEditor, 
  isRecoveryMode, 
  resetRecoveryMode,
  refreshEnvironmentDetection 
} from "./utils/environment";
import { enableBlankScreenAutoRecovery } from "./utils/blankScreenRecovery";
import { sessionRecovery } from "./utils/sessionRecovery";
import { errorLogger } from "./utils/errorLogger";
import { AdaptiveLoadingProvider } from "./components/AdaptiveLoadingProvider";
import { AppCacheManager } from "./components/AppCacheManager";
import { ErrorBoundary } from "./components/ui/error-boundary";
import { PWAProvider } from "./components/pwa/PWAProvider";

// Initialize resource hints as early as possible
initResourceHints();

// Preload logo and critical images - minimal subset only
preloadCriticalImages([
  '/lovable-uploads/98a9fcc4-c199-4be7-8bad-6756a3aa8870.png'
]);

// Enable automatic recovery from blank screens in Lovable editor
if (isLovableEditor()) {
  enableBlankScreenAutoRecovery();
}

// Initialize session recovery for idle timeout handling
sessionRecovery.initialize();

// Initialize error logging for better debugging
errorLogger.initialize();

function App() {
  const location = useLocation();
  
  // Initialize brand colors globally for all pages
  useBrandColors();
  
  // Add effect to measure navigation performance
  useEffect(() => {
    // Create a mark for route change
    const markName = `route-change-${location.pathname}`;
    performance.mark(markName);
    
    // Report route change performance
    setTimeout(() => {
      try {
        performance.measure(
          `route-navigation-${location.pathname}`, 
          markName
        );
        
        const measures = performance.getEntriesByType('measure');
        const currentMeasure = measures[measures.length - 1];
        
        if (currentMeasure) {
          console.log(`Navigation to ${location.pathname} took ${Math.round(currentMeasure.duration)}ms`);
        }
      } catch (e) {
        // Some browsers might not support this API
        console.warn('Performance API error:', e);
      }
    }, 100);
    
    return () => {
      // Clean up performance marks when component unmounts
      try {
        performance.clearMarks(markName);
        performance.clearMeasures(`route-navigation-${location.pathname}`);
      } catch (e) {
        // Some browsers might not support clearing specific marks
      }
    };
  }, [location.pathname]);
  
  // Handle recovery mode if active
  useEffect(() => {
    if (isRecoveryMode()) {
      console.log('[App] App mounted successfully in recovery mode, resetting recovery flag');
      resetRecoveryMode();
    }
    
    // Force refresh environment detection on app mount
    const detectedEnvironment = refreshEnvironmentDetection();
    console.log(`[App] Environment detection refreshed on mount: ${detectedEnvironment ? 'Lovable Editor' : 'Standard'}`);
    
    // Listen for HMR updates to maintain consistent environment detection
    if (import.meta.hot) {
      import.meta.hot.on('vite:beforeUpdate', () => {
        console.log('[App] HMR update starting, preserving environment detection...');
        try {
          sessionStorage.setItem('lamulamu_before_hmr_editor', 
            sessionStorage.getItem('lamulamu_is_lovable_editor') || 'false');
        } catch (e) {
          // Ignore storage errors
        }
      });
      
      import.meta.hot.on('vite:afterUpdate', () => {
        console.log('[App] HMR update completed, verifying environment detection...');
        try {
          const beforeHmr = sessionStorage.getItem('lamulamu_before_hmr_editor');
          const afterHmr = sessionStorage.getItem('lamulamu_is_lovable_editor');
          
          if (beforeHmr !== afterHmr) {
            console.warn('[App] Environment detection changed during HMR!', {
              before: beforeHmr,
              after: afterHmr
            });
            
            if (beforeHmr) {
              sessionStorage.setItem('lamulamu_is_lovable_editor', beforeHmr);
              console.log('[App] Restored previous environment detection');
            }
          }
          
          sessionStorage.removeItem('lamulamu_before_hmr_editor');
        } catch (e) {
          // Ignore storage errors
        }
      });
    }
  }, []);

  // Optimize rendering in Lovable editor
  const showDiagnostics = !isLovableEditor();

  return (
    <ErrorBoundary>
      <PWAProvider>
        <AdaptiveLoadingProvider>
          <AppCacheManager />
          <LovableEditorDetector />
          <ApplicationTitle defaultTitle="LamuLamu" />
          
          {showDiagnostics && (
            <>
              <PerformanceMonitor />
              <NetworkDiagnostics />
            </>
          )}
          
          <ProfileCheckProvider>
            <AppRoutes />
          </ProfileCheckProvider>
          
          <Toaster />
        </AdaptiveLoadingProvider>
      </PWAProvider>
    </ErrorBoundary>
  );
}

export default App;
